import { FaelligkeitsStufe, SifaMitarbeiter, SifaTeilnahme } from '../../services/sifa/types';
import { istBestanden } from './unterweisungModulLogic';

/**
 * Die Regeln der Mitarbeiter-Oberflaeche — rein, ohne DOM.
 * Die Faelligkeitsampel selbst (faelligkeitsStufe) liegt in
 * services/sifa/teilnahmeLogic.ts; hier steht nur, WELCHE Teilnahme je Person
 * zaehlt und wie gesucht und sortiert wird.
 */

export interface PersonenStand {
  /** Juengste BESTANDENE Teilnahme (nach Abschlussdatum). */
  letzte: SifaTeilnahme | null;
  /** Ablauf der juengsten bestandenen Teilnahme; null = keine oder ohne Ablauf. */
  gueltigBis: string | null;
  /** Alle Teilnahmen der Person, juengste zuerst. */
  historie: SifaTeilnahme[];
}

const zeit = (t: SifaTeilnahme): number => Date.parse(t.abgeschlossenAm || t.begonnenAm || '') || 0;

/** Stand je Mitarbeiter-id in einem Durchlauf. */
export const standJePerson = (teilnahmen: SifaTeilnahme[]): Record<string, PersonenStand> => {
  const out: Record<string, PersonenStand> = {};
  for (const t of teilnahmen || []) {
    if (!t.mitarbeiterId) continue;
    const s = (out[t.mitarbeiterId] ||= { letzte: null, gueltigBis: null, historie: [] });
    s.historie.push(t);
  }
  for (const s of Object.values(out)) {
    s.historie.sort((a, b) => zeit(b) - zeit(a));
    s.letzte = s.historie.find(istBestanden) || null;
    s.gueltigBis = s.letzte?.gueltigBis || null;
  }
  return out;
};

export const LEERER_STAND: PersonenStand = { letzte: null, gueltigBis: null, historie: [] };

/** Volltextsuche ueber Name, E-Mail, Abteilung, Personalnummer — Wortteile genuegen. */
export const sucheMitarbeiter = (liste: SifaMitarbeiter[], suche: string): SifaMitarbeiter[] => {
  const s = (suche || '').trim().toLowerCase();
  if (!s) return liste;
  const woerter = s.split(/\s+/);
  return liste.filter((m) => {
    const text = `${m.vorname} ${m.nachname} ${m.email || ''} ${m.abteilung || ''} ${m.personalnummer || ''}`.toLowerCase();
    return woerter.every((w) => text.includes(w));
  });
};

/** Nachname, Vorname — wie eine Personalliste. */
export const sortiereMitarbeiter = (liste: SifaMitarbeiter[]): SifaMitarbeiter[] =>
  [...liste].sort((a, b) =>
    (a.nachname || '').localeCompare(b.nachname || '', 'de') || (a.vorname || '').localeCompare(b.vorname || '', 'de'));

export const AMPEL: Record<FaelligkeitsStufe, { label: string; klasse: string; punkt: string }> = {
  gueltig: { label: 'gültig', klasse: 'text-emerald-700 bg-emerald-50 border-emerald-200', punkt: 'bg-emerald-500' },
  bald: { label: 'bald fällig', klasse: 'text-amber-700 bg-amber-50 border-amber-200', punkt: 'bg-amber-500' },
  abgelaufen: { label: 'abgelaufen', klasse: 'text-red-700 bg-red-50 border-red-200', punkt: 'bg-red-500' },
  ohne: { label: 'ohne Nachweis', klasse: 'text-slate-500 bg-slate-50 border-slate-200', punkt: 'bg-slate-300' },
};

/** Die Ampel einer Person: `ohne`, wenn es keine bestandene Teilnahme gibt — auch wenn die letzte „ohne Ablauf" waere. */
export const ampelStufe = (stand: PersonenStand, stufe: (gueltigBis: string | null) => FaelligkeitsStufe): FaelligkeitsStufe => {
  if (!stand.letzte) return 'ohne';
  return stufe(stand.gueltigBis);
};

/** Sortierschluessel fuer die Ampel: rot zuerst. */
export const AMPEL_RANG: Record<FaelligkeitsStufe, number> = { abgelaufen: 0, bald: 1, ohne: 2, gueltig: 3 };
